import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Stack,
  Spinner,
  Text,
  SimpleGrid,
  Card,
  Avatar,
  Badge,
  IconButton,
  Input,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { organizationsAPI, projectsAPI } from '../services/api';
import { Organization, Project } from '../types';
import { toast } from '../utils/toast';
import {
  getStoredLastOrgId,
  setStoredLastOrgId,
  getStoredOrgOrder,
  setStoredOrgOrder,
  reconcileOrgOrder,
  sortOrganizationsByUserOrder,
} from '../utils/organizationPreferences';
import { resolveLogoUrl } from '../utils/logoUrl';
import {
  formatProjectDate,
  getProjectCardTheme,
  PROJECT_CARD_THEME,
} from '../utils/projectCardTheme';
import { getStoredProjectViewMode, setStoredProjectViewMode, ProjectViewMode } from '../utils/projectViewPreferences';
import ProjectDashboardList from '../components/ProjectDashboardList';
import { ProjectViewToggle } from '../components/ProjectViewToggle';
import { MentionsBell } from '../components/MentionsBell';
import { hasProjectUpdatesSinceVisit } from '../utils/activityIndicators';
import { ActivityIndicatorBadge } from '../components/ActivityIndicatorBadge';
import { hydrateActivityFromProjects } from '../utils/activityStateStore';

interface OrgWithRole extends Organization {
  role?: 'owner' | 'admin' | 'member';
}

function organizationAcronym(name: string | undefined): string {
  if (!name) return 'ORG';
  const parts = name
    .split(/\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return 'ORG';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

function completionFilterLabel(
  key: 'all' | '0' | '1-24' | '25-49' | '50-74' | '75-99' | '100',
): string {
  switch (key) {
    case 'all':
      return 'All Projects';
    case '0':
      return '0%';
    case '1-24':
      return '1-24%';
    case '25-49':
      return '25-49%';
    case '50-74':
      return '50-74%';
    case '75-99':
      return '75-99%';
    case '100':
      return '100%';
    default:
      return 'All Projects';
  }
}

const Dashboard: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrgWithRole[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [completionFilter, setCompletionFilter] = useState<
    'all' | '0' | '1-24' | '25-49' | '50-74' | '75-99' | '100'
  >('all');
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileOrgPickerOpen, setMobileOrgPickerOpen] = useState(false);
  const [completionPickerOpen, setCompletionPickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, logout } = useAuth();
  const [projectViewMode, setProjectViewMode] = useState<ProjectViewMode>(() =>
    getStoredProjectViewMode(user?.id),
  );
  const navigate = useNavigate();
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const isAdmin = user?.role === 'admin';

  const renderNavButtons = () => {
    // Trenutno se koristi samo za horizontalni layout u headeru (ako je vidljiv),
    // pa zadržavamo jednostavnu verziju sa svim stavkama u nizu.
    return (
      <>
        <HStack gap={2}>
          {currentUserAvatarSrc ? (
            <img
              src={currentUserAvatarSrc}
              alt={user?.name || 'User avatar'}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '9999px',
                objectFit: 'cover',
                border: '1px solid #e5e7eb',
              }}
            />
          ) : (
            <Box
              width="32px"
              height="32px"
              borderRadius="full"
              bg="gray.200"
              color="black"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="sm"
              fontWeight="medium"
            >
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </Box>
          )}
          <Text fontWeight="medium" color="gray.700">
            {user?.name}
          </Text>
        </HStack>
        {(isMasterAdmin || isAdmin) && (
          <Button
            variant="outline"
            onClick={() => navigate('/admin')}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'md',
            }}
          >
            Admin Panel
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => navigate('/settings')}
          borderRadius="lg"
          _hover={{ bg: 'gray.100' }}
        >
          Settings
        </Button>
        <Button
          variant="outline"
          onClick={logout}
          borderRadius="lg"
          _hover={{
            bg: 'gray.100',
          }}
        >
          Logout
        </Button>
      </>
    );
  };

  const loadProjects = useCallback(async (orgId: string) => {
    try {
      const response = await projectsAPI.getByOrganization(orgId, showArchived);
      const list = Array.isArray(response.data) ? response.data : [];
      hydrateActivityFromProjects(list);
      setProjects(list);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load projects';
      toast({
        title: 'Error',
        description: msg,
        status: 'error',
      });
      setProjects([]);
    }
  }, [showArchived]);

  useEffect(() => {
    if (selectedOrg) {
      loadProjects(selectedOrg);
    }
  }, [selectedOrg, loadProjects]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedOrg) {
        loadProjects(selectedOrg);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [selectedOrg, loadProjects]);

  const loadOrganizations = useCallback(async () => {
    const uid = user?.id;
    if (!uid) {
      setOrganizations([]);
      setLoading(false);
      return;
    }
    try {
      const response = await organizationsAPI.getAll();
      const data = Array.isArray(response.data) ? response.data : [];
      const currentIds = data.map((o) => o._id);
      const order = reconcileOrgOrder(getStoredOrgOrder(uid), currentIds);
      setStoredOrgOrder(uid, order);
      const sorted = sortOrganizationsByUserOrder(data, order);
      setOrganizations(sorted);

      setSelectedOrg((prev) => {
        if (sorted.length === 0) return '';
        if (prev && sorted.some((o) => o._id === prev)) return prev;
        const lastId = getStoredLastOrgId(uid);
        if (lastId && sorted.some((o) => o._id === lastId)) return lastId;
        return sorted[0]._id;
      });
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load organizations';
      toast({
        title: 'Error',
        description: msg,
        status: 'error',
      });
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    setProjectViewMode(getStoredProjectViewMode(user?.id));
  }, [user?.id]);

  const handleProjectViewChange = (mode: ProjectViewMode) => {
    setProjectViewMode(mode);
    setStoredProjectViewMode(user?.id, mode);
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const filteredProjects = projects.filter((project) => {
    const pct = Math.max(0, Math.min(100, Number(project.taskCompletion?.percent ?? 0)));
    const byCompletion =
      completionFilter === 'all'
        ? true
        : completionFilter === '0'
          ? pct <= 0
          : completionFilter === '1-24'
            ? pct >= 1 && pct <= 24
            : completionFilter === '25-49'
              ? pct >= 25 && pct <= 49
              : completionFilter === '50-74'
                ? pct >= 50 && pct <= 74
                : completionFilter === '75-99'
                  ? pct >= 75 && pct <= 99
                  : pct >= 100;
    if (!byCompletion) return false;

    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    const name = (project.name || '').toLowerCase();
    return name.includes(term);
  });
  const selectedOrganization = organizations.find((o) => o._id === selectedOrg);
  const selectedOrgLogoSrc = resolveLogoUrl(selectedOrganization?.logoUrl);
  const currentUserAvatarSrc = resolveLogoUrl(user?.avatarUrl || null);

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top={0}
        left={0}
        h="100vh"
        w="260px"
        bg="white"
        borderRightWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        p={4}
        flexDirection="column"
        zIndex={15}
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.06}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src="/NS_Projects.png"
            alt="NS Projects watermark"
            style={{ maxWidth: '260px' }}
          />
        </Box>

        <Box
          position="relative"
          zIndex={1}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Heading
            size="lg"
            mb={2}
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Northstar Projects
          </Heading>
          <HStack gap={2} mb={6}>
            {currentUserAvatarSrc ? (
              <img
                src={currentUserAvatarSrc}
                alt={user?.name || 'User avatar'}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9999px',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb',
                }}
              />
            ) : (
              <Box
                width="32px"
                height="32px"
                borderRadius="full"
                bg="gray.200"
                color="black"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="sm"
                fontWeight="medium"
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Box>
            )}
            <Text fontWeight="medium" color="gray.700">
              {user?.name}
            </Text>
          </HStack>
          <VStack align="stretch" gap={3} flex="1">
            <MentionsBell variant="button" />
          </VStack>
          <VStack
            align="stretch"
            gap={2}
            pt={3}
            borderTopWidth={1}
            borderColor="gray.100"
          >
            {(isMasterAdmin || isAdmin) && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                justifyContent="flex-start"
                width="100%"
              >
                Admin Panel
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              borderRadius="lg"
              _hover={{ bg: 'gray.100' }}
              justifyContent="flex-start"
              width="100%"
            >
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              borderRadius="lg"
              _hover={{
                bg: 'gray.100',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Logout
            </Button>
          </VStack>
        </Box>
      </Box>

      {/* Mobile header */}
      <Box
        bg="white"
        borderBottomWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        display={{ base: 'block', md: 'none' }}
      >
        <Container maxW="container.xl" py={4}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={{ base: 3, md: 4 }}
          >
            <HStack w="100%" justifyContent="space-between" alignItems="center" gap={2}>
              <Heading
                size="lg"
                flex="1"
                minW={0}
                lineClamp={1}
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                Northstar Projects
              </Heading>
              <HStack gap={2} flexShrink={0} alignItems="center">
                <ProjectViewToggle mode={projectViewMode} onChange={handleProjectViewChange} size="xs" />
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="full"
                  px={3}
                  onClick={() => setMobileOrgPickerOpen(true)}
                >
                  <HStack gap={2}>
                    {selectedOrgLogoSrc ? (
                      <img
                        src={selectedOrgLogoSrc}
                        alt={selectedOrganization?.name || 'Company logo'}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '9999px',
                          objectFit: 'cover',
                          border: '1px solid #e5e7eb',
                        }}
                      />
                    ) : (
                      <Box
                        w="24px"
                        h="24px"
                        borderRadius="full"
                        bg="gray.200"
                        color="black"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {organizationAcronym(selectedOrganization?.name)}
                      </Box>
                    )}
                  </HStack>
                </Button>
                <IconButton
                  aria-label="Open navigation"
                  variant="ghost"
                  borderRadius="full"
                  display={{ base: 'inline-flex', md: 'none' }}
                  onClick={() => setMobileNavOpen(true)}
                >
                  <svg
                    width="22"
                    height="22"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </IconButton>
              </HStack>
            </HStack>
            {/* Horizontal nav (trenutno skriven na većim ekranima zajedno sa headerom) */}
          </Stack>
        </Container>
      </Box>

      {/* Organization picker overlay (mobile + desktop) */}
      {mobileOrgPickerOpen && (
        <Box>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.500"
            zIndex={30}
            onClick={() => setMobileOrgPickerOpen(false)}
          />
          <Box
            position="fixed"
            left={{ base: 0, md: '50%' }}
            right={{ base: 0, md: 'auto' }}
            bottom={{ base: 0, md: 'auto' }}
            top={{ base: 'auto', md: '50%' }}
            transform={{ base: 'none', md: 'translate(-50%, -50%)' }}
            zIndex={31}
            bg="white"
            borderTopRadius={{ base: '2xl', md: '2xl' }}
            borderRadius={{ base: '2xl 2xl 0 0', md: '2xl' }}
            boxShadow="2xl"
            p={4}
            width={{ base: '100%', md: '420px' }}
            maxH="70vh"
            overflowY="auto"
          >
            <HStack justifyContent="space-between" mb={3}>
              <Heading size="sm" color="gray.800">
                Select company
              </Heading>
              <IconButton
                aria-label="Close company picker"
                variant="ghost"
                borderRadius="full"
                onClick={() => setMobileOrgPickerOpen(false)}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </HStack>
            <VStack align="stretch" gap={2}>
              {organizations.map((org) => {
                const active = org._id === selectedOrg;
                return (
                  <Button
                    key={org._id}
                    variant={active ? 'solid' : 'outline'}
                    colorScheme={active ? 'blue' : 'gray'}
                    justifyContent="space-between"
                    borderRadius="lg"
                    onClick={() => {
                      setSelectedOrg(org._id);
                      setStoredLastOrgId(user?.id, org._id);
                      setMobileOrgPickerOpen(false);
                    }}
                  >
                    <HStack gap={2}>
                      {resolveLogoUrl(org.logoUrl) ? (
                        <img
                          src={resolveLogoUrl(org.logoUrl) || ''}
                          alt={`${org.name} logo`}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '9999px',
                            objectFit: 'cover',
                            border: active ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          w="24px"
                          h="24px"
                          borderRadius="full"
                          bg={active ? 'blue.100' : 'gray.200'}
                          color={active ? 'blue.800' : 'black'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xs"
                          fontWeight="semibold"
                          flexShrink={0}
                        >
                          {organizationAcronym(org.name)}
                        </Box>
                      )}
                      <Text>{org.name}</Text>
                    </HStack>
                  </Button>
                );
              })}
            </VStack>
          </Box>
        </Box>
      )}

      {completionPickerOpen && (
        <Box>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.500"
            zIndex={40}
            onClick={() => setCompletionPickerOpen(false)}
          />
          <Box
            position="fixed"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            zIndex={41}
            bg="white"
            borderRadius="2xl"
            boxShadow="2xl"
            p={4}
            width={{ base: '92vw', md: '420px' }}
            maxH="70vh"
            overflowY="auto"
          >
            <HStack justifyContent="space-between" mb={3}>
              <Heading size="sm" color="gray.800">
                Filter projects by completion
              </Heading>
              <IconButton
                aria-label="Close completion filter"
                variant="ghost"
                borderRadius="full"
                onClick={() => setCompletionPickerOpen(false)}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </HStack>
            <VStack align="stretch" gap={2}>
              {(['all', '0', '1-24', '25-49', '50-74', '75-99', '100'] as const).map((opt) => {
                const active = completionFilter === opt;
                return (
                  <Button
                    key={opt}
                    variant={active ? 'solid' : 'outline'}
                    colorScheme={active ? 'blue' : 'gray'}
                    justifyContent="space-between"
                    borderRadius="lg"
                    onClick={() => {
                      setCompletionFilter(opt);
                      setCompletionPickerOpen(false);
                    }}
                  >
                    <Text>{completionFilterLabel(opt)}</Text>
                  </Button>
                );
              })}
            </VStack>
          </Box>
        </Box>
      )}

      {/* Mobile sidebar navigation */}
      {mobileNavOpen && (
        <Box display={{ base: 'block', md: 'none' }}>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.400"
            zIndex={20}
            onClick={() => setMobileNavOpen(false)}
          />
          <Box
            position="fixed"
            top={0}
            left={0}
            h="100vh"
            w="260px"
            bg="white"
            boxShadow="xl"
            zIndex={21}
            p={4}
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset={0}
              opacity={0.06}
              pointerEvents="none"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src="/NS_Projects.png"
                alt="NS Projects watermark"
                style={{ maxWidth: '260px' }}
              />
            </Box>

            <VStack align="stretch" gap={4} h="100%" position="relative" zIndex={1}>
              <HStack justifyContent="space-between" mb={1}>
                <Heading size="md" color="gray.800">
                  Menu
                </Heading>
                <IconButton
                  aria-label="Close navigation"
                  variant="ghost"
                  borderRadius="full"
                  onClick={() => setMobileNavOpen(false)}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </IconButton>
              </HStack>
              <HStack gap={2} mb={2}>
                {currentUserAvatarSrc ? (
                  <img
                    src={currentUserAvatarSrc}
                    alt={user?.name || 'User avatar'}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '9999px',
                      objectFit: 'cover',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                ) : (
                  <Box
                    width="32px"
                    height="32px"
                    borderRadius="full"
                    bg="gray.200"
                    color="black"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Box>
                )}
                <Text fontWeight="medium" color="gray.700">
                  {user?.name}
                </Text>
              </HStack>
              <VStack align="stretch" gap={3} flex="1">
                <MentionsBell variant="button" />
              </VStack>
              <VStack
                align="stretch"
                gap={2}
                pt={2}
                borderTopWidth={1}
                borderColor="gray.100"
              >
                {(isMasterAdmin || isAdmin) && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin')}
                    borderRadius="lg"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                    }}
                    justifyContent="flex-start"
                    width="100%"
                  >
                    Admin Panel
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings')}
                  borderRadius="lg"
                  _hover={{ bg: 'gray.100' }}
                  justifyContent="flex-start"
                  width="100%"
                >
                  Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                  borderRadius="lg"
                  _hover={{
                    bg: 'gray.100',
                  }}
                  justifyContent="flex-start"
                  width="100%"
                >
                  Logout
                </Button>
              </VStack>
            </VStack>
          </Box>
        </Box>
      )}

      <Container
        maxW={{ base: 'container.xl', md: '100%' }}
        py={8}
        pl={{ base: 4, md: '280px' }}
        pr={{ base: 4, md: 8 }}
      >
        <VStack gap={6} align="stretch">
          {/* Controls */}
          <Stack
            direction={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'stretch', md: 'center' }}
            justifyContent="space-between"
            gap={4}
            flexWrap="wrap"
          >
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              flex="1"
              minW={0}
            >
              <VStack
                display={{ base: 'none', md: 'flex' }}
                align="stretch"
                gap={1}
                maxW={{ base: '100%', md: '280px' }}
                flexShrink={0}
              >
                <Button
                  variant="outline"
                  borderRadius="lg"
                  justifyContent="space-between"
                  onClick={() => setMobileOrgPickerOpen(true)}
                >
                  <HStack gap={2}>
                    {selectedOrgLogoSrc ? (
                      <img
                        src={selectedOrgLogoSrc}
                        alt={selectedOrganization?.name || 'Company logo'}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '9999px',
                          objectFit: 'cover',
                          border: '1px solid #e5e7eb',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        w="24px"
                        h="24px"
                        borderRadius="full"
                        bg="gray.200"
                        color="black"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="xs"
                        fontWeight="semibold"
                        flexShrink={0}
                      >
                        {organizationAcronym(selectedOrganization?.name)}
                      </Box>
                    )}
                    <Text truncate maxW="160px">
                      {selectedOrganization?.name || 'Select company'}
                    </Text>
                  </HStack>
                </Button>
                {/* <Text fontSize="xs" color="gray.500">
                  Last company and list order are remembered. Reorder in{' '}
                  <Text
                    as="span"
                    color="blue.600"
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={() => navigate('/settings')}
                  >
                    Settings
                  </Text>
                  .
                </Text> */}
              </VStack>
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="lg"
                maxW={{ base: '100%', md: '400px' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
              />
              <Box display={{ base: 'none', md: 'block' }}>
                <ProjectViewToggle mode={projectViewMode} onChange={handleProjectViewChange} />
              </Box>
            </Stack>
            <SimpleGrid
              columns={3}
              gap={2}
              flexShrink={0}
              width={{ base: '100%', md: 'auto' }}
              minChildWidth={0}
            >
              <Button
                variant="outline"
                width="100%"
                borderRadius="lg"
                px={{ base: 2, md: 4 }}
                fontSize={{ base: 'xs', md: 'sm' }}
                onClick={() => setCompletionPickerOpen(true)}
              >
                {completionFilterLabel(completionFilter)}
              </Button>
              <Button
                variant={showArchived ? 'solid' : 'outline'}
                onClick={() => setShowArchived(!showArchived)}
                borderRadius="lg"
                colorScheme="gray"
                width="100%"
                px={{ base: 2, md: 4 }}
                fontSize={{ base: 'xs', md: 'sm' }}
              >
                {showArchived ? 'Hide' : 'Archived'}
              </Button>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={() => navigate('/project/new')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                fontWeight="semibold"
                width="100%"
                px={{ base: 2, md: 4 }}
                fontSize={{ base: 'xs', md: 'sm' }}
              >
                New Project
              </Button>
            </SimpleGrid>
          </Stack>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <Box 
              textAlign="center" 
              py={20}
              bg="white"
              borderRadius="xl"
              borderWidth={2}
              borderStyle="dashed"
              borderColor="gray.300"
            >
              <Box mb={4}>
                <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto', color: '#9CA3AF' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </Box>
              <Text fontSize="xl" fontWeight="semibold" color="gray.600" mb={2}>
                No projects yet
              </Text>
              <Text fontSize="md" color="gray.500" mb={6}>
                Create your first project to get started!
              </Text>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={() => navigate('/project/new')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
              >
                Create Project
              </Button>
            </Box>
          ) : projectViewMode === 'list' ? (
            <ProjectDashboardList
              projects={filteredProjects}
              userId={user?.id}
              onProjectClick={handleProjectClick}
            />
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
              {filteredProjects.map((project) => {
                const cardThemeKey = getProjectCardTheme(project);
                const palette = PROJECT_CARD_THEME[cardThemeKey];
                const hasUpdates = hasProjectUpdatesSinceVisit(project, user?.id);
                return (
                <Card.Root
                  key={project._id}
                  cursor="pointer"
                  onClick={() => handleProjectClick(project._id)}
                  bg={palette.bg}
                  borderRadius="xl"
                  borderWidth={palette.borderWidth}
                  borderColor={hasUpdates ? 'blue.200' : palette.border}
                  overflow="hidden"
                  transition="all 0.3s"
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: 'xl',
                    borderColor: hasUpdates ? 'blue.300' : palette.borderHover,
                  }}
                >
                  <Box
                    h="4px"
                    bgGradient="to-r"
                    gradientFrom={palette.topFrom}
                    gradientTo={palette.topTo}
                  />
                  <Card.Body p={6}>
                    <HStack justifyContent="space-between" mb={3} align="flex-start" gap={2} flexWrap="wrap">
                      <Heading size="md" color="gray.800">
                        {project.name}
                      </Heading>
                      <HStack gap={2} flexShrink={0}>
                        {hasUpdates && <ActivityIndicatorBadge label="New activity" variant="project" />}
                        {cardThemeKey === 'critical' && (
                          <Badge colorScheme="red" borderRadius="full" px={2}>
                            Past end date
                          </Badge>
                        )}
                        {cardThemeKey === 'warning' && (
                          <Badge colorScheme="orange" borderRadius="full" px={2}>
                            At risk
                          </Badge>
                        )}
                        {cardThemeKey === 'success' && (
                          <Badge colorScheme="green" borderRadius="full" px={2}>
                            Complete
                          </Badge>
                        )}
                        <Badge 
                          colorScheme={project.status === 'archived' ? 'gray' : 'green'} 
                          borderRadius="full" 
                          px={2}
                        >
                          {project.status === 'archived' ? 'Archived' : 'Active'}
                        </Badge>
                      </HStack>
                    </HStack>
                    <Text 
                      color="gray.600" 
                      fontSize="sm"
                      mb={4}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.5'
                      } as React.CSSProperties}
                    >
                      {project.description || 'No description provided'}
                    </Text>
                    <HStack gap={6} mb={4} flexWrap="wrap">
                      <Box minW={0}>
                        <Text fontSize="xs" color="gray.500" mb={0.5}>
                          Start date
                        </Text>
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                          {formatProjectDate(project.startDate)}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text
                          fontSize="xs"
                          color={
                            cardThemeKey === 'critical'
                              ? 'red.600'
                              : cardThemeKey === 'warning'
                                ? 'orange.600'
                                : 'gray.500'
                          }
                          mb={0.5}
                          fontWeight={cardThemeKey === 'critical' || cardThemeKey === 'warning' ? 'semibold' : 'normal'}
                        >
                          End date
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={
                            cardThemeKey === 'critical'
                              ? 'red.700'
                              : cardThemeKey === 'warning'
                                ? 'orange.800'
                                : 'gray.700'
                          }
                        >
                          {formatProjectDate(project.endDate)}
                        </Text>
                      </Box>
                    </HStack>
                    <Box>
                      <HStack justifyContent="space-between" mb={1}>
                        <Text fontSize="xs" color="gray.500">
                          Completed
                        </Text>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                          {project.taskCompletion?.percent ?? 0}%
                        </Text>
                      </HStack>
                      <Box h="9px" bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box
                          h="100%"
                          w={`${Math.min(100, Math.max(0, project.taskCompletion?.percent ?? 0))}%`}
                          bgGradient="to-r"
                          gradientFrom={palette.progressFrom}
                          gradientTo={palette.progressTo}
                          borderRadius="full"
                          transition="width 0.35s ease-out"
                        />
                      </Box>
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        {(project.taskCompletion?.completed ?? 0)} / {(project.taskCompletion?.total ?? 0)}{' '}
                        tasks finished
                      </Text>
                    </Box>
                  </Card.Body>
                </Card.Root>
                );
              })}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default Dashboard;
